import { Box, Chip, Typography } from '@mui/material'
import { formatApiDate } from '~/shared/lib/date'
import { parseISO } from 'date-fns'
import partition from 'lodash/partition'
import { FC, Fragment } from 'react'
import { AvailablePhysicianSlot } from '../AppointmentService'
import { getDisplayBookingWindowInWeeks } from './bookingWindowUtils'
import { required } from '~/shared/lib/required'

const FULL_DATE_STR_FORMAT = 'EEE, MMM d'

const SlotNotePill = ({ text }: { text: string }) => (
  <Box sx={{ bgcolor: 'tones.neutral.bg', display: 'inline-flex', p: 1, flexWrap: 'wrap' }}>
    <Typography sx={{ fontSize: '1.3rem', color: 'navy.500', fontWeight: 500 }}>{text}</Typography>
  </Box>
)

export const NoAvailability = ({ bookingWindowInHours }: { bookingWindowInHours?: number }) => {
  const displayWeeks = getDisplayBookingWindowInWeeks(bookingWindowInHours) ?? 0
  return <SlotNotePill text={`No availability in the next ${displayWeeks} weeks`} />
}
type FirstAvailableProps = {
  firstAvailableSlot: string
  setDate: (value: Date) => void
}

export const FirstAvailable = ({ firstAvailableSlot, setDate }: FirstAvailableProps) => {
  return (
    <Box
      sx={{
        bgcolor: 'tones.neutral.bg',
        display: 'inline-flex',
        p: 1,
        flexWrap: 'wrap',
        flexDirection: 'row',
      }}
    >
      <Typography
        sx={{
          fontSize: '1.3rem',
          color: 'navy.500',
          fontWeight: 500,
        }}
      >
        {'First available:'}&nbsp;
      </Typography>
      <Typography
        sx={{
          fontSize: '1.3rem',
          color: 'navy.500',
          fontWeight: 500,
          textDecorationLine: 'underline',
        }}
        onClick={() => {
          let date = new Date()
          if (firstAvailableSlot) {
            date = new Date(firstAvailableSlot)
            if (
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              date.getFullYear() === new Date().getFullYear()
            ) {
              date = new Date()
            } else {
              date.setHours(0)
              date.setMinutes(0)
              date.setSeconds(0)
              date.setMilliseconds(0)
            }
          }
          setDate(date)
        }}
      >
        {formatApiDate(firstAvailableSlot, FULL_DATE_STR_FORMAT) ?? ''}
      </Typography>
    </Box>
  )
}

/** What each physician's slots collapse to when the day's rows are grouped by provider. */
type PhysicianSlotGroup = {
  slots: NonNullable<AvailablePhysicianSlot['slots']>
  physicianId: AvailablePhysicianSlot['physician_id']
  reason: AvailablePhysicianSlot['reason']
  firstAvailableSlot: AvailablePhysicianSlot['first_available_slot']
}

type PhysicianSlotPickerGroupByDayProps = {
  setDate: (value: Date) => void
  slots: AvailablePhysicianSlot[]
  value: AvailablePhysicianSlot | null
  onChange: (slot: AvailablePhysicianSlot) => void
  isCareTeamSlots: boolean
  bookingWindowInHours?: number
  careTeamProviderNames?: string[]
}

const PhysicianSlotPickerGroupByDay: FC<PhysicianSlotPickerGroupByDayProps> = ({
  setDate,
  slots,
  value,
  onChange,
  isCareTeamSlots,
  bookingWindowInHours,
  careTeamProviderNames,
}) => {
  const slotsByPhysician = slots.reduce(
    (app, physician) => {
      if (physician.physician_name && physician.slots) {
        app[physician.physician_name] = {
          slots: physician.slots,
          physicianId: physician.physician_id,
          reason: physician.reason,
          firstAvailableSlot: physician.first_available_slot,
        }
      }
      return app
    },
    {} as Record<string, PhysicianSlotGroup>,
  )

  const sorted = Object.keys(slotsByPhysician).sort() // alphabetical order of physicians

  return (
    <>
      {sorted.length
        ? sorted.map(displayName => {
            const {
              slots: physicianSlots,
              physicianId,
              reason,
              firstAvailableSlot,
            } = required(slotsByPhysician[displayName], `slots for ${displayName}`)
            const slotsEl = physicianSlots.length ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  marginBottom: 2,
                  marginTop: 1,
                  flexWrap: 'wrap',
                  rowGap: 1,
                }}
              >
                <PhysicianSlotPicker
                  slots={physicianSlots}
                  physicianId={physicianId}
                  reason={reason}
                  inCareTeam={isCareTeamSlots}
                  value={value}
                  onChange={onChange}
                />
              </Box>
            ) : firstAvailableSlot ? (
              <FirstAvailable firstAvailableSlot={firstAvailableSlot} setDate={setDate} />
            ) : (
              <NoAvailability bookingWindowInHours={bookingWindowInHours} />
            )
            return physicianSlots.length || isCareTeamSlots ? (
              <Fragment key={displayName}>
                <Typography sx={{ fontSize: '1.4rem', marginTop: 2, fontWeight: 500 }}>
                  {displayName}
                </Typography>
                {slotsEl}
              </Fragment>
            ) : null
          })
        : // `length > 0` keeps an empty list from rendering as a literal "0".
          careTeamProviderNames != null &&
          careTeamProviderNames.length > 0 &&
          careTeamProviderNames.map((displayName: string, i: number) => {
            return (
              <Fragment key={i}>
                <Typography sx={{ fontSize: '1.4rem', marginTop: 2, fontWeight: 500 }}>
                  {displayName}
                </Typography>
                <NoAvailability bookingWindowInHours={bookingWindowInHours} />
              </Fragment>
            )
          })}
    </>
  )
}

type PhysicianSlotPickerProps = {
  slots: string[]
  physicianId: number
  reason: string
  inCareTeam: boolean
  value: AvailablePhysicianSlot | null
  onChange: (slot: AvailablePhysicianSlot) => void
}

export const PhysicianSlotPicker: FC<PhysicianSlotPickerProps> = ({
  slots,
  physicianId,
  reason,
  inCareTeam,
  value,
  onChange,
}) => {
  return (
    <>
      {slots.map(slot => {
        const time = parseISO(slot).toLocaleTimeString(undefined, {
          hour12: true,
          hour: '2-digit',
          minute: '2-digit',
        })
        return (
          <Fragment key={`${physicianId}-${slot}`}>
            <Chip
              label={time}
              onClick={() =>
                onChange({
                  scheduled_date: slot,
                  physician_id: physicianId,
                  reason: reason,
                  in_care_team: inCareTeam,
                })
              }
              color={
                value?.physician_id === physicianId && value.scheduled_date === slot
                  ? 'primary'
                  : 'default'
              }
              variant="outlined"
              sx={{
                padding: 0,
                marginRight: 1,
                '&.MuiButtonBase-root.MuiChip-colorPrimary': {
                  backgroundColor: 'navy.500',
                  color: 'common.white',
                  borderColor: 'navy.500',
                },
                '&.MuiButtonBase-root.MuiChip-colorDefault': {
                  backgroundColor: 'transparent',
                },
              }}
            />
          </Fragment>
        )
      })}
    </>
  )
}

type PhysicianSlotPickerGroupsByDayProps = {
  setDate: (value: Date) => void
  slots: AvailablePhysicianSlot[]
  value: AvailablePhysicianSlot | null
  onChange: (slot: AvailablePhysicianSlot) => void
  bookingWindowInHours?: number
  careTeamProviderNames: string[]
}

const PhysicianSlotPickerGroupsByDay: FC<PhysicianSlotPickerGroupsByDayProps> = props => {
  const slots = props.slots
  const [careTeamSlots, othersSlots] = partition(slots, slot => slot.in_care_team)
  // Show other providers title only when there is atleast one provider with an available slot

  // With neither slots nor names, the care team has nobody eligible for the
  // visit type — different from eligible-but-booked, which lists names with a
  // no-availability box.
  const careTeamEl = (
    <Box
      sx={{
        marginTop: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: '1.3rem',
          color: 'grey.600',
        }}
      >
        {"Patient's care team:"}
      </Typography>
      {careTeamSlots.length || props.careTeamProviderNames.length ? (
        <PhysicianSlotPickerGroupByDay
          {...props}
          slots={careTeamSlots}
          isCareTeamSlots={true}
          bookingWindowInHours={props.bookingWindowInHours}
        />
      ) : (
        <SlotNotePill text="No care team provider offers this visit type" />
      )}
    </Box>
  )

  const slotsByPhysician = othersSlots.reduce(
    (app, physician) => {
      if (physician.physician_name && physician.slots) {
        app[physician.physician_name] = {
          slots: physician.slots,
          physicianId: physician.physician_id,
          reason: physician.reason,
          firstAvailableSlot: physician.first_available_slot,
        }
      }
      return app
    },
    {} as Record<string, PhysicianSlotGroup>,
  )
  const atleastOneSlotAvailable = Object.values(slotsByPhysician).some(
    group => group.slots.length > 0,
  )

  const othersEl =
    othersSlots.length && atleastOneSlotAvailable ? (
      <Box
        sx={{
          marginTop: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.3rem',
            color: 'grey.600',
          }}
        >
          {"Other providers licensed in patient's state:"}
        </Typography>
        <PhysicianSlotPickerGroupByDay {...props} slots={othersSlots} isCareTeamSlots={false} />
      </Box>
    ) : null

  return (
    <>
      {(slots.length == 0 || (careTeamSlots.length === 0 && !atleastOneSlotAvailable)) &&
      props.careTeamProviderNames.length == 0 ? (
        // Should show below text when there is no care team provider or other providers available for the selected visit type
        <Typography sx={{ marginTop: 1 }}>No clinician available</Typography>
      ) : (
        <Box>
          {careTeamEl}
          {othersEl}
        </Box>
      )}
    </>
  )
}

export default PhysicianSlotPickerGroupsByDay
